import { alpha, PaletteOptions, TypeBackground } from '@mui/material'
import {
  AccentBlue,
  AccentBlueDark,
  AccentGreen,
  AccentGreenDark,
  AccentOrange,
  AccentOrangeDark,
  AccentPurple,
  AccentPurpleDark,
  AccentTeal,
  AccentTealDark,
  BeeCrewGrey,
  BeeCrewYellow,
  BeeCrewYellowDark,
  ElevationDark,
  ElevationLight,
  TypographyDark,
  TypographyLight,
} from './colors'

interface ColorOptions {
  main: string
  mainHover: string
  mainContrastText: string
  light?: string
  lightHover?: string
  lightContrastText?: string
  dark?: string
  darkHover?: string
  darkContrastText?: string
  text?: string
  textHover?: string
}

interface TextOptions {
  highEmphasis: string
  mediumEmphasis: string
  base: string
  disabled: string
}

interface BackgroundOptions extends Partial<TypeBackground> {
  main: string
  elevation1: string
  elevation2: string
  elevation3: string
  elevation4: string
  elevation5: string
  elevation6: string
  elevation7: string
  elevation8: string
  elevation9: string
}

interface AccentColors {
  blue: ColorOptions
  purple: ColorOptions
  teal: ColorOptions
  green: ColorOptions
  orange: ColorOptions
}

export interface ExtendedPaletteOptions extends PaletteOptions {
  primary: ColorOptions
  secondary: ColorOptions
  disabled: ColorOptions
  background: BackgroundOptions
  text: TextOptions
  accent: AccentColors
  warning: ColorOptions
}

export const paletteLight: ExtendedPaletteOptions = {
  primary: {
    main: BeeCrewYellow[400],
    mainHover: BeeCrewYellow[500],
    mainContrastText: BeeCrewGrey[950],
    light: BeeCrewYellow[200],
    lightHover: BeeCrewYellow[300],
    lightContrastText: BeeCrewGrey[950],
    dark: BeeCrewYellow[800],
    darkHover: BeeCrewYellow[900],
    darkContrastText: '#FFFFFF',
  },
  secondary: {
    main: BeeCrewGrey[200],
    mainHover: BeeCrewGrey[300],
    mainContrastText: BeeCrewGrey[950],
    light: BeeCrewGrey[100],
    lightHover: BeeCrewGrey[200],
    lightContrastText: BeeCrewGrey[950],
    dark: BeeCrewGrey[600],
    darkHover: BeeCrewGrey[700],
    darkContrastText: '#FFFFFF',
  },
  disabled: {
    // TODO: Do sprawdzenia
    main: BeeCrewGrey[100],
    mainHover: BeeCrewGrey[200],
    mainContrastText: BeeCrewGrey[300],
  },
  background: {
    main: ElevationLight[50],
    elevation1: ElevationLight[50],
    elevation2: ElevationLight[100],
    elevation3: ElevationLight[200],
    elevation4: ElevationLight[300],
    elevation5: ElevationLight[400],
    elevation6: ElevationLight[500],
    elevation7: ElevationLight[600],
    elevation8: ElevationLight[700],
    elevation9: ElevationLight[800],
  },
  text: {
    highEmphasis: TypographyLight[300],
    mediumEmphasis: TypographyLight[100],
    base: TypographyLight[200],
    disabled: TypographyLight[50],
  },
  accent: {
    blue: {
      main: AccentBlue[300],
      mainHover: AccentBlue[500],
      mainContrastText: '#FFFFFF',
      light: AccentBlue[100],
      lightHover: AccentBlue[200],
      lightContrastText: AccentBlue[900],
    },
    purple: {
      main: AccentPurple[300],
      mainHover: AccentPurple[500],
      mainContrastText: '#FFFFFF',
      light: AccentPurple[100],
      lightHover: AccentPurple[300],
      lightContrastText: AccentPurple[900],
    },
    teal: {
      main: AccentTeal[500],
      mainHover: AccentTeal[700],
      mainContrastText: '#FFFFFF',
      light: AccentTeal[100],
      lightHover: AccentTeal[300],
      lightContrastText: AccentTeal[900],
    },
    green: {
      main: AccentGreen[950],
      mainHover: AccentGreen[500],
      mainContrastText: '#FFFFFF',
      light: AccentGreen[100],
      lightHover: AccentGreen[300],
      lightContrastText: AccentGreen[900],
    },
    orange: {
      main: AccentOrange[950],
      mainHover: AccentOrange[600],
      mainContrastText: '#FFFFFF',
      light: AccentOrange[100],
      lightHover: AccentOrange[300],
      lightContrastText: AccentOrange[900],
      text: AccentOrange[600],
      textHover: AccentOrange[700],
    },
  },
  warning: {
    main: '#e32417',
    mainHover: '#D33328',
    mainContrastText: '#FFFFFF',
    light: alpha('#EA392D', 0.7)
  },
}

export const paletteDark: ExtendedPaletteOptions = {
  primary: {
    main: BeeCrewYellowDark[500],
    mainHover: BeeCrewYellowDark[600],
    mainContrastText: BeeCrewGrey[950],
    light: BeeCrewYellowDark[300],
    lightHover: BeeCrewYellowDark[400],
    lightContrastText: BeeCrewGrey[950],
    dark: BeeCrewYellowDark[700],
    darkHover: BeeCrewYellowDark[800],
    darkContrastText: BeeCrewGrey[50],
  },
  secondary: {
    main: BeeCrewGrey[500],
    mainHover: BeeCrewGrey[400],
    mainContrastText: BeeCrewGrey[950],
    light: BeeCrewGrey[400],
    lightHover: BeeCrewGrey[300],
    lightContrastText: '#000000',
  },
  disabled: {
    // TODO: Do sprawdzenia
    main: BeeCrewGrey[800],
    mainHover: BeeCrewGrey[700],
    mainContrastText: BeeCrewGrey[600],
  },
  background: {
    main: ElevationDark[50],
    elevation1: ElevationDark[100],
    elevation2: ElevationDark[200],
    elevation3: ElevationDark[300],
    elevation4: ElevationDark[400],
    elevation5: ElevationDark[500],
    elevation6: ElevationDark[600],
    elevation7: ElevationDark[700],
    elevation8: ElevationDark[800],
    elevation9: ElevationDark[900],
  },
  text: {
    highEmphasis: TypographyDark[50],
    mediumEmphasis: TypographyDark[100],
    base: TypographyDark[200],
    disabled: TypographyDark[400],
  },
  accent: {
    blue: {
      main: AccentBlueDark[600],
      mainHover: AccentBlueDark[700],
      mainContrastText: '#FFFFFF',
      light: AccentBlueDark[200],
      lightHover: AccentBlueDark[300],
      lightContrastText: AccentBlueDark[900],
    },
    purple: {
      main: AccentPurpleDark[500],
      mainHover: AccentPurpleDark[700],
      mainContrastText: '#FFFFFF',
      light: AccentPurpleDark[50],
      lightHover: AccentPurpleDark[100],
      lightContrastText: AccentPurpleDark[900],
    },
    teal: {
      main: AccentTealDark[500],
      mainHover: AccentTealDark[600],
      mainContrastText: '#000000',
      light: AccentTealDark[50],
      lightHover: AccentTealDark[100],
      lightContrastText: AccentTealDark[900],
    },
    green: {
      main: AccentGreenDark[400],
      mainHover: AccentGreenDark[500],
      mainContrastText: '#000000',
      light: AccentGreenDark[50],
      lightHover: AccentGreenDark[100],
      lightContrastText: AccentGreenDark[400],
    },
    orange: {
      main: AccentOrangeDark[200],
      mainHover: AccentOrangeDark[400],
      mainContrastText: '#000000',
      light: AccentOrangeDark[100],
      lightHover: AccentOrangeDark[200],
      lightContrastText: AccentOrangeDark[900],
      text: AccentOrangeDark[100],
      textHover: AccentOrangeDark[300],
    },
  },
  warning: {
    main: '#f85448',
    mainHover: '#c84137',
    mainContrastText: '#FFFFFF',
    light: alpha('#f85448', 0.7)
  },
}
